import { useState, useEffect } from "react";

import { X } from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import MoviesCarousel from "../components/MoviesCarousel";
import SeriesCarousel from "../components/SeriesCarousel";
import apiService from "../services/api";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerCastDetails = () => {
  const { castId } = useParams();
  const [cast, setCast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("movies");
  const [showImageOverlay, setShowImageOverlay] = useState(false);
  const [showBioOverlay, setShowBioOverlay] = useState(false);

  // Get profile and playlist from storage
  const profile = JSON.parse(sessionStorage.getItem("selectedProfile") || "{}");
  const playlist = JSON.parse(localStorage.getItem("selectedPlaylist") || "{}");

  useEffect(() => {
    async function fetchCastDetails() {
      try {
        setLoading(true);
        const response = await apiService.webPlayerGetCastDetails(castId);

        if (response.data) {
          setCast(response.data);
          // Set initial tab based on what content is available
          if (response.data.movies?.length > 0) {
            setActiveTab("movies");
          } else if (response.data.series?.length > 0) {
            setActiveTab("series");
          }
        }
      } catch (error) {
        console.error("Error fetching cast details:", error);
        toast.error("Failed to load cast details");
      } finally {
        setLoading(false);
      }
    }

    if (castId) {
      fetchCastDetails();
    }
  }, [castId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500" />
      </div>
    );
  }

  if (!cast) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white text-xl">Cast member not found</p>
      </div>
    );
  }

  const moviesCount = cast.movies?.length || 0;
  const seriesCount = cast.series?.length || 0;

  return (
    <div className="min-h-screen text-white relative">
      {/* Fixed gradient background */}
      <div className="fixed inset-0 bg-gradient-to-b from-gray-600 via-gray-800 to-black z-0" />

      {/* Dashboard Button - Bottom Right */}
      <button
        className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
        onClick={() => {
          webPlayerStorage.clearAll();
          window.location.href = "https://dashboard.lunova.app";
        }}
      >
        Dashboard
      </button>

      {/* Left Menu */}
      <LeftMenu activeMenuItem="" playlist={playlist} profile={profile} />

      {/* Mobile Menu */}
      <MobileMenu />

      {/* Main Content */}
      <div className="relative z-10 ml-0 lg:ml-20 px-4 sm:px-6 lg:px-8 py-12 pt-16 lg:pt-12">
        {/* Header Section - Profile Image and Info */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-12">
          {/* Profile Image */}
          <div
            className="flex-shrink-0 cursor-pointer group mx-auto sm:mx-0"
            role="button"
            tabIndex={0}
            onClick={() => cast.profilePath && setShowImageOverlay(true)}
            onKeyDown={activateOnEnterOrSpace}
          >
            <div
              className="w-32 sm:w-40 lg:w-48 h-auto rounded-xl overflow-hidden border-2 border-gray-700 group-hover:border-gray-500 transition-colors"
              style={{
                boxShadow: "0 10px 30px -10px rgba(0,0,0,0.5)",
              }}
            >
              {cast.profilePath ? (
                <img
                  alt={cast.name}
                  className="w-full h-auto object-cover"
                  src={cast.profilePath}
                />
              ) : (
                <div className="w-full aspect-[2/3] bg-gray-700 flex items-center justify-center">
                  <svg
                    className="w-20 h-20 text-gray-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      fillRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Info Section */}
          <div className="flex-1 text-center sm:text-left">
            {/* Name */}
            <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4">
              {cast.name}
            </h1>

            {/* Biography */}
            {cast.biography && (
              <div
                className="mb-4 cursor-pointer hover:bg-white/5 rounded-lg p-2 -ml-2 transition-colors"
                role="button"
                tabIndex={0}
                onClick={() => setShowBioOverlay(true)}
                onKeyDown={activateOnEnterOrSpace}
              >
                <p className="text-gray-300 text-base leading-relaxed line-clamp-5">
                  {cast.biography}
                </p>
              </div>
            )}

            {/* Birthday */}
            {cast.birthday && (
              <p className="text-gray-400 text-sm mb-1">
                <span className="font-semibold text-white">Birthday:</span>{" "}
                {cast.birthday}
              </p>
            )}

            {/* Place of Birth */}
            {cast.placeOfBirth && (
              <p className="text-gray-400 text-sm">
                <span className="font-semibold text-white">
                  Place of Birth:
                </span>{" "}
                {cast.placeOfBirth}
              </p>
            )}
          </div>
        </div>

        {/* Tabs Section */}
        <div className="flex gap-2 sm:gap-4 mb-6">
          <button
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all ${
              activeTab === "movies"
                ? "bg-white text-black"
                : "bg-gray-800 text-white hover:bg-gray-700"
            } ${moviesCount === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
            disabled={moviesCount === 0}
            onClick={() => moviesCount > 0 && setActiveTab("movies")}
          >
            Movies ({moviesCount})
          </button>
          <button
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all ${
              activeTab === "series"
                ? "bg-white text-black"
                : "bg-gray-800 text-white hover:bg-gray-700"
            } ${seriesCount === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
            disabled={seriesCount === 0}
            onClick={() => seriesCount > 0 && setActiveTab("series")}
          >
            Series ({seriesCount})
          </button>
        </div>

        {/* Content Grid */}
        <div className="-mr-8">
          {activeTab === "movies" && cast.movies && cast.movies.length > 0 && (
            <MoviesCarousel items={cast.movies} itemsVisible={6.5} />
          )}

          {activeTab === "series" && cast.series && cast.series.length > 0 && (
            <SeriesCarousel items={cast.series} itemsVisible={6.5} />
          )}

          {/* Empty state */}
          {((activeTab === "movies" && moviesCount === 0) ||
            (activeTab === "series" && seriesCount === 0)) && (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">
                No {activeTab} found for {cast.name}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Image Overlay */}
      {showImageOverlay && cast.profilePath && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          role="presentation"
          onClick={() => setShowImageOverlay(false)}
        >
          {/* Backdrop with blur */}
          <div
            className="absolute inset-0"
            style={{
              backdropFilter: "blur(20px)",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            }}
          />

          {/* Close button */}
          <button
            className="absolute top-6 right-6 z-10 text-white hover:text-gray-300 transition-colors"
            onClick={() => setShowImageOverlay(false)}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Large Image */}
          <div
            className="relative z-10 max-w-[50vw] max-h-[85vh]"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              alt={cast.name}
              className="max-w-full max-h-[85vh] object-contain rounded-xl"
              src={cast.profilePath}
              style={{
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
              }}
            />
          </div>
        </div>
      )}

      {/* Biography Overlay */}
      {showBioOverlay && cast.biography && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-8"
          role="presentation"
          onClick={() => setShowBioOverlay(false)}
        >
          {/* Backdrop with blur */}
          <div
            className="absolute inset-0"
            style={{
              backdropFilter: "blur(20px)",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            }}
          />

          {/* Close button */}
          <button
            className="absolute top-6 right-6 z-10 text-white hover:text-gray-300 transition-colors"
            onClick={() => setShowBioOverlay(false)}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Biography Content */}
          <div
            className="relative z-10 max-w-3xl max-h-[80vh] overflow-y-auto rounded-xl p-8"
            role="presentation"
            style={{
              backgroundColor: "rgba(30, 30, 30, 0.95)",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-white text-lg leading-relaxed whitespace-pre-line">
              {cast.biography}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebPlayerCastDetails;
